import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react'
import { Text } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import { InvoiceApprovalStatus } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { InvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'
import BaseInvoiceOverview, { ExtendedInvoiceOverviewDto, InvoiceFilterType } from '../Finance/Invoice/BaseInvoiceOverview'
import { InvoiceType } from '../../config/finance/invoiceConfig'

export type ViewInvoiceApprovalInvoicesProps = ViewInvoiceApprovalComponentProps & {
    setInvoicesCount: Dispatch<SetStateAction<number>>
    setInvoices: Dispatch<SetStateAction<ExtendedInvoiceOverviewDto[]>>
    selectedInvoices: ExtendedInvoiceOverviewDto[]
}

export default function ViewInvoiceApprovalInvoices(props: ViewInvoiceApprovalInvoicesProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    // Deposits are not bound to the template period, so no from/to filter.
    const defaultFilterValues = useMemo<InvoiceFilterType>(
        () => ({
            from: '',
            to: undefined,
            types: [InvoiceType.Deposit],
            isSettled: props.approvalStatus === InvoiceApprovalStatus.Done ? undefined : false,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
            attributes: [],
            query: '',
        }),
        [values, props.approvalStatus],
    )

    const fixedFilterValues = useMemo<InvoiceFilterType>(
        () => ({
            files: values.fileIds || [],
            types: [InvoiceType.Deposit],
            isInvoiceApproval: true,
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseInvoiceOverview
                    hideFileField
                    hideStatusField
                    focusQueryOnRender={false}
                    noSticky
                    fileId={values.fileIds?.length === 1 ? values.fileIds[0] : undefined}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(_column, result) => props.setInvoicesCount(result.total)}
                    onLoadItems={(_column, _columnPages, items) => props.setInvoices([...items])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedInvoices.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedInvoices.includes(item)).length === 0}
                    useItemSelectedColor={false}
                    // Deposits are no user revenue items: the add/remove endpoints do not create
                    // UserInvoiceApprovals, so unlike the registration tabs there is no response to
                    // merge into userApprovals.
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('invoices', [...values.invoices, item.id])
                            if (values.id) {
                                axios.post(`/finance/invoice-approval/${values.id}/invoice?id=${item.id}`)
                            }
                        } else {
                            setFieldValue('invoices', [...values.invoices.filter(invoice => invoice !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/invoice?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'invoices',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios.post(`/finance/invoice-approval/${values.id}/invoice?id=${items.map(item => item.id).join(',')}`)
                            }
                        } else {
                            setFieldValue('invoices', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/invoice?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedInvoices}
                />
            )}
        </div>
    )
}
